import React from "react";
import {
  UserIcon,
  PhoneIcon,
  MailIcon,
  BadgeCheckIcon,
  ShoppingCartIcon,
  CurrencyRupeeIcon,
  TagIcon,
  ReceiptTaxIcon,
  RefreshIcon,
  CashIcon,
} from "@heroicons/react/outline";

const OverviewTab = ({
  order,
  formatCurrency,
  formatDate,
  hasExchange,
  totalExchangeValue,
  hasRefund,
}) => {
  // Calculate summary stats
  const summaryStats = {
    totalItems: order.orderSummary?.totalItems || 0,
    uniqueItems: order.orderSummary?.uniqueItems || 0,
    subtotal:
      order.orderSummary?.finalTotals?.subtotal ||
      order.orderSummary?.baseTotals?.subtotal ||
      0,
    discount: order.orderSummary?.finalTotals?.totalDiscount || 0,
    gst:
      order.orderSummary?.finalTotals?.totalGST ||
      order.orderSummary?.baseTotals?.totalGST ||
      0,
    grandTotal: order.orderSummary?.finalTotals?.grandTotal || 0,
    amountDue: Math.abs(order.orderSummary?.amountDue || 0),
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Customer Information Card */}
      <div className="bg-white rounded-xl sm:rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="px-3 sm:px-4 md:px-5 py-2.5 sm:py-3 md:py-4 bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-100">
          <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 flex items-center gap-2">
            <div className="p-1.5 sm:p-2 rounded-lg bg-primary/10">
              <UserIcon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            </div>
            Customer Information
          </h3>
        </div>

        <div className="p-3 sm:p-4 md:p-5">
          {/* Customer Avatar & Name */}
          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-lg sm:text-xl font-bold text-primary">
                {(order.userId?.name || order.guestCustomer?.name || "G")
                  .charAt(0)
                  .toUpperCase()}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <h4 className="font-semibold text-gray-900 text-sm sm:text-base md:text-lg truncate">
                {order.userId?.name ||
                  order.guestCustomer?.name ||
                  "Guest Customer"}
              </h4>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] sm:text-xs font-medium">
                  {order.userId?.accountType || "B2C"}
                </span>
                {order.userId && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-[10px] sm:text-xs font-medium">
                    <BadgeCheckIcon className="w-3 h-3 mr-0.5" />
                    Registered
                  </span>
                )}
                {!order.userId && order.guestCustomer && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] sm:text-xs font-medium">
                    Guest
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Contact Details Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {/* Phone */}
            <div className="flex items-center gap-3 p-2.5 sm:p-3 bg-gray-50 rounded-lg sm:rounded-xl">
              <div className="p-2 rounded-lg bg-white border border-gray-200 flex-shrink-0">
                <PhoneIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs text-gray-500 font-medium">
                  Phone
                </p>
                <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                  {order.userId?.phoneNumber ||
                    order.guestCustomer?.phoneNumber ||
                    "Not provided"}
                </p>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-center gap-3 p-2.5 sm:p-3 bg-gray-50 rounded-lg sm:rounded-xl">
              <div className="p-2 rounded-lg bg-white border border-gray-200 flex-shrink-0">
                <MailIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs text-gray-500 font-medium">
                  Email
                </p>
                <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                  {order.userId?.email ||
                    order.guestCustomer?.email ||
                    "Not provided"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Order Summary Card */}
      <div className="bg-white rounded-xl sm:rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="px-3 sm:px-4 md:px-5 py-2.5 sm:py-3 md:py-4 bg-gradient-to-r from-gray-50 to-amber-50 border-b border-gray-100">
          <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 flex items-center gap-2">
            <div className="p-1.5 sm:p-2 rounded-lg bg-amber-100">
              <ShoppingCartIcon className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
            </div>
            Order Summary
          </h3>
        </div>

        <div className="p-3 sm:p-4 md:p-5">
          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4 sm:mb-5">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-blue-100">
              <p className="text-[10px] sm:text-xs text-blue-600 font-medium mb-0.5 sm:mb-1">
                Total Items
              </p>
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-blue-900">
                {summaryStats.totalItems}
              </p>
              <p className="text-[10px] sm:text-xs text-blue-600 mt-0.5">
                {summaryStats.uniqueItems} unique
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-green-100">
              <p className="text-[10px] sm:text-xs text-green-600 font-medium mb-0.5 sm:mb-1">
                Grand Total
              </p>
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-green-900">
                {formatCurrency(summaryStats.grandTotal)}
              </p>
              {summaryStats.discount > 0 && (
                <p className="text-[10px] sm:text-xs text-green-600 mt-0.5">
                  Saved {formatCurrency(summaryStats.discount)}
                </p>
              )}
            </div>
          </div>

          {/* Price Breakdown */}
          <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4 space-y-2 sm:space-y-3">
            {/* Subtotal */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-600">
                <CurrencyRupeeIcon className="w-4 h-4 text-gray-400 hidden sm:block" />
                <span className="text-xs sm:text-sm">Subtotal</span>
              </div>
              <span className="text-xs sm:text-sm font-semibold text-gray-900">
                {formatCurrency(summaryStats.subtotal)}
              </span>
            </div>

            {/* Discount */}
            {summaryStats.discount > 0 && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-red-600">
                  <TagIcon className="w-4 h-4 text-red-400 hidden sm:block" />
                  <span className="text-xs sm:text-sm">Discount</span>
                </div>
                <span className="text-xs sm:text-sm font-semibold text-red-600">
                  -{formatCurrency(summaryStats.discount)}
                </span>
              </div>
            )}

            {/* GST */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-600">
                <ReceiptTaxIcon className="w-4 h-4 text-gray-400 hidden sm:block" />
                <span className="text-xs sm:text-sm">GST</span>
              </div>
              <span className="text-xs sm:text-sm font-semibold text-gray-900">
                {formatCurrency(summaryStats.gst)}
              </span>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 pt-2 sm:pt-3">
              <div className="flex items-center justify-between">
                <span className="text-sm sm:text-base font-bold text-gray-900">
                  Grand Total
                </span>
                <span className="text-base sm:text-lg md:text-xl font-bold text-primary">
                  {formatCurrency(summaryStats.grandTotal)}
                </span>
              </div>
            </div>

            {/* Exchange Section */}
            {hasExchange && (
              <>
                <div className="border-t border-dashed border-gray-300 pt-2 sm:pt-3 mt-2 sm:mt-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-green-600">
                      <RefreshIcon className="w-4 h-4 text-green-500 hidden sm:block" />
                      <span className="text-xs sm:text-sm font-medium">
                        Exchange Value
                      </span>
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-green-600">
                      -{formatCurrency(totalExchangeValue)}
                    </span>
                  </div>
                </div>

                {/* Amount Due / Refund */}
                <div
                  className={`mt-2 sm:mt-3 p-2.5 sm:p-3 rounded-lg sm:rounded-xl ${
                    hasRefund
                      ? "bg-orange-50 border border-orange-200"
                      : "bg-red-50 border border-red-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CashIcon
                        className={`w-4 h-4 sm:w-5 sm:h-5 ${
                          hasRefund ? "text-orange-500" : "text-red-500"
                        }`}
                      />
                      <span
                        className={`text-sm sm:text-base font-bold ${
                          hasRefund ? "text-orange-900" : "text-red-900"
                        }`}
                      >
                        {hasRefund ? "Refund Due" : "Amount Due"}
                      </span>
                    </div>
                    <span
                      className={`text-base sm:text-lg md:text-xl font-bold ${
                        hasRefund ? "text-orange-600" : "text-red-600"
                      }`}
                    >
                      {formatCurrency(summaryStats.amountDue)}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Order Info Footer */}
      {(order.createdAt || order.orderNumber) && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-1 text-[10px] sm:text-xs text-gray-400">
          {order.orderNumber && (
            <span>
              Order #{" "}
              <span className="font-mono text-gray-500">
                {order.orderNumber}
              </span>
            </span>
          )}
          {order.createdAt && (
            <span>
              Created on{" "}
              <span className="text-gray-500">
                {formatDate(order.createdAt)}
              </span>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default OverviewTab;
